import React from 'react';
import { X } from 'lucide-react';
import { MeetingTemplate, MeetingType, ParticipantRole } from '@/types/meeting';

interface TemplateFormProps {
  template?: MeetingTemplate;
  onSubmit: (template: Partial<MeetingTemplate>) => void;
  onClose: () => void;
}

export function TemplateForm({ template, onSubmit, onClose }: TemplateFormProps) {
  const [formData, setFormData] = React.useState<Partial<MeetingTemplate>>(
    template || {
      name: '',
      type: 'PARENT_TEACHER',
      purpose: '',
      description: '',
      requiredRoles: [],
      defaultDuration: 30,
      agendaTemplate: []
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {template ? 'Edit Template' : 'Create Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 max-h-[calc(100vh-12rem)] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Template Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Meeting Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as MeetingType }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="PARENT_TEACHER">Parent Teacher Conference</option>
                <option value="DISCIPLINARY">Disciplinary Meeting</option>
                <option value="ACADEMIC_REVIEW">Academic Review</option>
                <option value="TUTORING">Tutoring Session</option>
                <option value="STAFF">Staff Meeting</option>
                <option value="DEPARTMENT">Department Meeting</option>
                <option value="IEP">IEP Meeting</option>
                <option value="COUNSELING">Counseling Session</option>
                <option value="CLUB">Club Meeting</option>
                <option value="ADMINISTRATIVE">Administrative Meeting</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Purpose</label>
              <textarea
                value={formData.purpose}
                onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Default Duration (minutes)</label>
              <input
                type="number"
                value={formData.defaultDuration}
                onChange={(e) => setFormData(prev => ({ ...prev, defaultDuration: parseInt(e.target.value) }))}
                min="15"
                step="15"
                className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Required Roles</label>
              <div className="space-y-2">
                {Object.values(ParticipantRole).map((role) => (
                  <label key={role} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.requiredRoles?.includes(role)}
                      onChange={(e) => {
                        const roles = formData.requiredRoles || [];
                        setFormData(prev => ({
                          ...prev,
                          requiredRoles: e.target.checked
                            ? [...roles, role]
                            : roles.filter(r => r !== role)
                        }));
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {role.charAt(0) + role.slice(1).toLowerCase().replace('_', ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
            >
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}